/**
 * ExamGuard — anti-leak wrapper for exam content.
 *
 * Three deterrents against question text spreading:
 *   1. Tiled watermark (student name + id + date) over the content, so any
 *      screenshot is traceable back to the person who leaked it.
 *   2. Copy / cut / context-menu / text-selection blocking.
 *   3. A blur "curtain" the moment the tab/window loses focus, so a student
 *      can't alt-tab to a search engine while the questions stay readable.
 *
 * None of these are unbreakable (a determined user with devtools/another
 * device can bypass them) — they raise the effort and make casual leaking
 * traceable, which is the realistic goal for an online exam.
 */

import { useEffect, useRef, useState } from 'react'
import { EyeOff } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'

/* XML-escape so a name with <, >, & can't break the inline SVG watermark. */
function esc(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function buildWatermark(label) {
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='340' height='200'>` +
    `<text x='20' y='100' transform='rotate(-28 170 100)' ` +
    `fill='rgba(130,130,130,0.14)' font-size='15' font-family='sans-serif' ` +
    `font-weight='600'>${esc(label)}</text></svg>`
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`
}

export default function ExamGuard({ children }) {
  const { user } = useAuth()
  const rootRef = useRef(null)
  const [obscured, setObscured] = useState(false)

  // ── Hide content when the tab/window is not in focus ────────────────
  useEffect(() => {
    const hide = () => setObscured(true)
    const show = () => setObscured(false)
    const onVisibility = () =>
      setObscured(document.visibilityState !== 'visible')

    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('blur', hide)
    window.addEventListener('focus', show)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('blur', hide)
      window.removeEventListener('focus', show)
    }
  }, [])

  // ── Block copy / cut / right-click / selection on the content ───────
  // Answer fields (essay / short-answer) must stay fully editable, so copy /
  // cut / selection are allowed when the event originates inside a form field.
  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const isField = (t) =>
      t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)
    const prevent = (e) => {
      if (isField(e.target) && e.type !== 'contextmenu' && e.type !== 'dragstart') return
      e.preventDefault()
    }
    const events = ['copy', 'cut', 'contextmenu', 'selectstart', 'dragstart']
    events.forEach((ev) => el.addEventListener(ev, prevent))
    return () => events.forEach((ev) => el.removeEventListener(ev, prevent))
  }, [])

  const name  = user?.full_name || user?.username || 'Foydalanuvchi'
  const stamp = new Date().toLocaleDateString()
  const label = `${name} · ID ${user?.id ?? '—'} · ${stamp}`

  return (
    <div
      ref={rootRef}
      className="exam-guard relative"
      style={{
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
      }}
    >
      {children}

      {/* Watermark — tiled, non-interactive so inputs stay clickable */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-10"
        style={{
          backgroundImage: buildWatermark(label),
          backgroundRepeat: 'repeat',
        }}
      />

      {/* Focus-loss curtain */}
      {obscured && (
        <div
          className="fixed inset-0 z-[60] flex flex-col items-center justify-center gap-3 text-center px-6"
          style={{ background: 'rgba(15,15,18,0.92)', backdropFilter: 'blur(8px)' }}
        >
          <EyeOff size={40} className="text-gray-300" />
          <p className="text-gray-100 text-base font-semibold m-0">
            Imtihon oynasidan chiqdingiz
          </p>
          <p className="text-gray-400 text-sm m-0">
            Savollarni ko'rish uchun shu oynaga qaytib bosing.
          </p>
        </div>
      )}
    </div>
  )
}
